/**
 * Wraps `GET /me` — the JIT user bootstrap endpoint. First call on a
 * fresh JWT creates the `users` row in Relay's DB; subsequent calls
 * just return the profile.
 *
 * 5-minute staleTime matches how rarely a user's role/tenant changes;
 * invalidate from a role-management UI when that exists.
 */

import { useQuery } from '@tanstack/react-query';

import { client } from '@/api/client';
import { qk } from '@/api/query-keys';

export interface MeUser {
  id: string;
  keycloak_sub: string;
  email: string;
  display_name?: string;
  role: string;
  is_active: boolean;
  /**
   * Populated when this principal is a worker-self-service account
   * linked to a `workers` row; null otherwise (admins / staff).
   */
  worker_id: string | null;
}

export interface MeAgency {
  id: string;
  name: string;
}

export interface MeResponse {
  user: MeUser;
  staffing_agency: MeAgency;
  /**
   * v0.0.66.1 — effective per-feature access for this user. Computed
   * server-side from role defaults + `user_feature_overrides`. Use
   * `useAuth().hasFeature(key)` to gate UI elements; never trust
   * client-side derivation since the backend re-checks on every call.
   */
  features?: Record<string, boolean>;
}

export function useMe() {
  return useQuery({
    queryKey: qk.me.current(),
    queryFn: async (): Promise<MeResponse> => {
      const { data, error } = await client.GET('/me' as never);
      if (error) throw new Error('Failed to load /me');
      return data as MeResponse;
    },
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    retry: 1,
  });
}
